/**
 * Helper utilities for styled-components v6 prop filtering
 * Prevents styled-system props from being passed to DOM elements
 */

// Space props (margin and padding)
const spaceProps = [
  'm',
  'mt',
  'mr',
  'mb',
  'ml',
  'mx',
  'my',
  'margin',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft',
  'marginX',
  'marginY',
  'p',
  'pt',
  'pr',
  'pb',
  'pl',
  'px',
  'py',
  'padding',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'paddingX',
  'paddingY',
]

// Layout props
const layoutProps = [
  'width',
  'height',
  'minWidth',
  'maxWidth',
  'minHeight',
  'maxHeight',
  'display',
  'size',
  'verticalAlign',
  'overflow',
  'overflowX',
  'overflowY',
]

// Flexbox props
const flexboxProps = [
  'alignItems',
  'alignContent',
  'justifyItems',
  'justifyContent',
  'flexWrap',
  'flexDirection',
  'flex',
  'flexGrow',
  'flexShrink',
  'flexBasis',
  'justifySelf',
  'alignSelf',
  'order',
]

// Color props
const colorProps = ['color', 'bg', 'backgroundColor', 'opacity']

// Position props
const positionProps = ['position', 'zIndex', 'top', 'right', 'bottom', 'left']

// Typography props
const typographyProps = [
  'fontSize',
  'fontStyle',
  'fontFamily',
  'fontWeight',
  'letterSpacing',
  'lineHeight',
  'textAlign',
  'textStyle',
  'textTransform',
  'whiteSpace',
  'textOverflow',
  'wordBreak',
]

// Border props
const borderProps = ['borderRadius', 'borderColor']

// Pre-built Sets for common use cases
export const SPACE_PROPS = new Set(spaceProps)
export const LAYOUT_PROPS = new Set(layoutProps)
export const FLEXBOX_PROPS = new Set(flexboxProps)
export const COLOR_PROPS = new Set(colorProps)
export const POSITION_PROPS = new Set(positionProps)
export const TYPOGRAPHY_PROPS = new Set(typographyProps)
export const BORDER_PROPS = new Set(borderProps)

// Combined sets for common patterns
export const SPACE_AND_LAYOUT_PROPS = new Set([...spaceProps, ...layoutProps])
export const SPACE_AND_COLOR_PROPS = new Set([...spaceProps, ...colorProps])
export const ALL_STYLED_SYSTEM_PROPS = new Set([
  ...spaceProps,
  ...layoutProps,
  ...flexboxProps,
  ...colorProps,
  ...positionProps,
  ...typographyProps,
  ...borderProps,
])

/**
 * Creates a shouldForwardProp function for styled-components
 * @param propsToFilter - Set of prop names to filter out, or array of additional props
 * @param baseSet - Optional base set to extend (defaults to SPACE_PROPS)
 */
export const createShouldForwardProp = (propsToFilter: Set<string> | string[], baseSet?: Set<string>): ((prop: string) => boolean) => {
  if (Array.isArray(propsToFilter)) {
    const combinedSet = baseSet ? new Set([...baseSet, ...propsToFilter]) : new Set([...SPACE_PROPS, ...propsToFilter])
    return (prop: string) => !combinedSet.has(prop)
  }
  return (prop: string) => !propsToFilter.has(prop)
}

/**
 * Shorthand for filtering space props + custom props
 * @param customProps - Additional props to filter
 */
export const filterSpaceProps = (...customProps: string[]): ((prop: string) => boolean) => {
  if (customProps.length === 0) {
    return (prop: string) => !SPACE_PROPS.has(prop)
  }
  const combined = new Set([...SPACE_PROPS, ...customProps])
  return (prop: string) => !combined.has(prop)
}

/**
 * Shorthand for filtering layout props + custom props
 */
export const filterLayoutProps = (...customProps: string[]): ((prop: string) => boolean) => {
  const combined = new Set([...SPACE_PROPS, ...LAYOUT_PROPS, ...customProps])
  return (prop: string) => !combined.has(prop)
}

/**
 * Shorthand for filtering all common styled-system props + custom props
 */
export const filterStyledSystemProps = (...customProps: string[]): ((prop: string) => boolean) => {
  const combined = new Set([...ALL_STYLED_SYSTEM_PROPS, ...customProps])
  return (prop: string) => !combined.has(prop)
}
